import React, { useState, useEffect } from 'react';
import { AiFillEye, AiFillGithub } from 'react-icons/ai';
import { MdArrowBack, MdClose } from 'react-icons/md';
import { HiLightBulb, HiCode, HiBan, HiAcademicCap } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { images } from '../../constants';
import projects from '../../data/projects.json';
import './AllProjects.scss';

/**
 * Renders a story field that is either:
 *   - a plain string  (legacy)
 *   - an array of blocks: { type: 'text'|'list'|'callout', content?, items? }
 */
const StoryContent = ({ content }) => {
  if (!content) return null;

  // Legacy: plain string
  if (typeof content === 'string') {
    return <p className="ap__story-text">{content}</p>;
  }

  // Rich block array
  return (
    <div className="ap__story-blocks">
      {content.map((block, i) => {
        if (block.type === 'text') {
          return <p key={i} className="ap__story-text">{block.content}</p>;
        }
        if (block.type === 'list') {
          return (
            <ul key={i} className="ap__story-list">
              {block.items.map((item, j) => (
                <li key={j} className="ap__story-list-item">{item}</li>
              ))}
            </ul>
          );
        }
        if (block.type === 'callout') {
          return (
            <div key={i} className="ap__story-callout">
              {block.content}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};

const STORY_SECTIONS = [
  {
    key: 'problem',
    icon: <HiLightBulb />,
    label: 'The Problem',
    color: '#f59e0b',
  },
  {
    key: 'howIStarted',
    icon: <HiCode />,
    label: 'How I Started',
    color: '#6366f1',
  },
  {
    key: 'challenge',
    icon: <HiBan />,
    label: 'A Challenge I Hit',
    color: '#ef4444',
  },
  {
    key: 'learnings',
    icon: <HiAcademicCap />,
    label: 'What I Learned',
    color: '#10b981',
  },
];

const CATEGORIES = ['All', 'Web', 'Mobile', 'Data Science', 'Artificial Intelligence', 'CLI'];

const AllProjects = () => {
  const [works, setWorks] = useState([]);
  const [filterWork, setFilterWork] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [animateCard, setAnimateCard] = useState({ opacity: 1, y: 0 });
  const [selectedWork, setSelectedWork] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const withImages = projects.filter((p) => p.imgUrl !== '');
    setWorks(withImages);
    setFilterWork(withImages);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selectedWork ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedWork]);

  const handleWorkFilter = (item) => {
    setActiveFilter(item);
    setAnimateCard({ opacity: 0, y: 20 });
    setTimeout(() => {
      setAnimateCard({ opacity: 1, y: 0 });
      setFilterWork(item === 'All' ? works : works.filter((w) => w.tags.includes(item)));
    }, 400);
  };

  return (
    <div className="ap__page">
      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="ap__header">
        <button className="ap__back-btn" onClick={() => navigate('/')}>
          <MdArrowBack />
          <span>Home</span>
        </button>
        <div className="ap__header-text">
          <h1 className="ap__title">All Projects</h1>
          <p className="ap__subtitle">Click any card to read the full story behind the project</p>
        </div>
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="ap__filters">
        {CATEGORIES.map((cat, i) => (
          <button
            key={i}
            className={`ap__filter-btn ${activeFilter === cat ? 'active' : ''}`}
            onClick={() => handleWorkFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* ── Grid ─────────────────────────────────────────────────── */}
      <motion.div
        className="ap__grid"
        animate={animateCard}
        transition={{ duration: 0.4 }}
      >
        {filterWork.map((work, index) => (
          <motion.div
            className="ap__card"
            key={work.name}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.06 }}
            onClick={() => setSelectedWork(work)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedWork(work)}
          >
            {/* Image */}
            <div className="ap__card-img">
              <img src={images[work.imgUrl]} alt={work.title} loading="lazy" />
              <div className="ap__card-overlay">
                <span className="ap__card-read">Read Story →</span>
              </div>
            </div>

            {/* Body */}
            <div className="ap__card-body">
              <div className="ap__card-tag">{work.tags[0]}</div>
              <h3 className="ap__card-title">{work.title}</h3>
              <p className="ap__card-summary">{work.summary}</p>
              <div className="ap__card-tech">{work.tech}</div>
            </div>

            {/* Footer links */}
            {(work.projectLink || work.codeLink) && (
              <div className="ap__card-links" onClick={(e) => e.stopPropagation()}>
                {work.projectLink && (
                  <a href={work.projectLink} target="_blank" rel="noreferrer" className="ap__link-btn">
                    <AiFillEye /> Live
                  </a>
                )}
                {work.codeLink && (
                  <a href={work.codeLink} target="_blank" rel="noreferrer" className="ap__link-btn">
                    <AiFillGithub /> Code
                  </a>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* ── Modal ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {selectedWork && (
          <motion.div
            className="ap__modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedWork(null)}
          >
            <motion.div
              className="ap__modal"
              initial={{ opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 40 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="ap__modal-header">
                <img
                  className="ap__modal-hero"
                  src={images[selectedWork.imgUrl]}
                  alt={selectedWork.title}
                />
                <div className="ap__modal-header-overlay">
                  <span className="ap__modal-tag">{selectedWork.tags[0]}</span>
                  <h2 className="ap__modal-title">{selectedWork.title}</h2>
                  <p className="ap__modal-tech">{selectedWork.tech}</p>
                  <div className="ap__modal-actions">
                    {selectedWork.projectLink && (
                      <a href={selectedWork.projectLink} target="_blank" rel="noreferrer" className="ap__modal-action-btn">
                        <AiFillEye /> View Live
                      </a>
                    )}
                    {selectedWork.codeLink && (
                      <a href={selectedWork.codeLink} target="_blank" rel="noreferrer" className="ap__modal-action-btn secondary">
                        <AiFillGithub /> Source Code
                      </a>
                    )}
                  </div>
                </div>
                <button className="ap__modal-close" onClick={() => setSelectedWork(null)}>
                  <MdClose />
                </button>
              </div>

              {/* Story sections */}
              <div className="ap__modal-body">
                {STORY_SECTIONS.map((section, i) => (
                  selectedWork[section.key] && (
                    <motion.div
                      key={section.key}
                      className="ap__story-section"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                    >
                      <div className="ap__story-icon" style={{ color: section.color, borderColor: section.color + '33', background: section.color + '15' }}>
                        {section.icon}
                      </div>
                      <div className="ap__story-content">
                        <h4 className="ap__story-label" style={{ color: section.color }}>
                          {section.label}
                        </h4>
                        <StoryContent content={selectedWork[section.key]} />
                      </div>
                    </motion.div>
                  )
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllProjects;
